import { inject, onUnmounted } from 'vue'
import { ORG_CHANNEL_KEY } from '@/composables/useOrganizationChannel'

// Live-update glue (doc/LIVE_UPDATES.md — "signal, not data"): silently
// refetch when the org channel signals a change to any of `types`. `match`
// narrows to specific ids (a detail page follows only its own records);
// without it any signal of the type refetches (a list view). Call from a
// component-bound composable's setup. No channel provided (unit mounts, the
// public entrypoint) ⇒ a no-op — live updates are an enhancement, never a
// dependency.
//
// ⚠ It never fires for THIS tab's own writes: the channel drops that echo
// (lib/socketId), so whatever calls this must also refetch after its own write.
//
// ⭐ ONE WAVE AT A TIME, AND ONE FOLLOW-UP. Another tab's act that touches
// several records broadcasts several — so firing a refetch per signal stacked
// identical requests whose answers could only agree. Everything asked for during a wave collapses into exactly ONE
// follow-up, which is what makes the last answer the true one: the queued wave
// starts AFTER the running one finished, so it cannot be the stale read.
//
// The collapser is per call site: two surfaces watching the same types keep
// their own queues, because they are refetching different things.
export function useLiveRefetch(
  types: string[],
  refetch: () => unknown,
  match?: (update: { type: string; id: string }) => boolean,
) {
  const channel = inject(ORG_CHANNEL_KEY, null)
  if (!channel) return

  let running: Promise<unknown> | null = null
  let queued: Promise<unknown> | null = null

  // ⚠ `Promise.resolve().then(refetch)` rather than calling it bare: `refetch`
  // may return void or throw synchronously, and either would break the chain
  // and strand `running` as a promise that never settles — after which this
  // surface would stop following live updates entirely, silently. Failures are
  // swallowed HERE only: every caller's refetch reports its own errors through
  // its API composable, so there is nothing left for this layer to say.
  const runOnce = () => Promise.resolve().then(refetch).catch(() => {})

  const wave = (): Promise<unknown> => {
    if (!running) {
      running = runOnce().finally(() => { running = null })
      return running
    }
    queued ??= running.then(() => { queued = null; return wave() })
    return queued
  }

  const unsubscribe = channel.onMessage(({ updates }) => {
    if (updates.some(u => types.includes(u.type) && (match ? match(u) : true))) {
      void wave()
    }
  })
  onUnmounted(unsubscribe)
}
